import { normalizeSessionLabel } from "@/lib/enrollment";

/** One resolved component of a multi-session registration (display only). */
export interface SessionResolution {
  class_payment_id: string;
  original_session_label: string;
  resolution_status: string;
  reason: string;
  historical_period: string | null;
  offering_ran: boolean | null;
  financial_disposition: string;
  admin_note: string | null;
  resolved_at: string;
}

const key = (paymentId: string, label: string) =>
  `${paymentId}|${(normalizeSessionLabel(label) ?? label).trim().toLowerCase()}`;

export function indexResolutions(rows: SessionResolution[]): Map<string, SessionResolution> {
  const m = new Map<string, SessionResolution>();
  for (const r of rows) m.set(key(r.class_payment_id, r.original_session_label), r);
  return m;
}

/** Resolution for exactly this payment + component label, if recorded. */
export function findResolution(
  index: Map<string, SessionResolution>,
  paymentId: string,
  componentLabel: string | null | undefined,
): SessionResolution | null {
  if (!componentLabel) return null;
  return index.get(key(paymentId, componentLabel)) ?? null;
}

export function describeResolution(r: SessionResolution): string {
  if (isFinanciallyOpen(r)) {
    const what = r.financial_disposition === "refund_pending" ? "Refund due" : "Credit due";
    return `${what} — not yet issued. ${r.historical_period ? r.historical_period + ": " : ""}${r.reason}`;
  }
  const ran = r.offering_ran === false ? " — not offered / did not run" : "";
  const period = r.historical_period ? `${r.historical_period}: ` : "";
  return `Resolved ${r.resolved_at.slice(0, 10)}${ran}. ${period}${r.reason}`;
}

/** Pending money outcomes keep the component in Needs Resolution. */
export function isFinanciallyOpen(r: SessionResolution): boolean {
  return r.financial_disposition === "refund_pending" || r.financial_disposition === "credit_pending";
}
